/**
 * Split-payment row builder.
 *
 * Used by every flow that creates earnings rows for a multi-payment
 * package (AddEarningsDialog, Clients re-up, ClientDetail re-up). The
 * three callsites had identical inline logic — extracted here so the
 * bug we just fixed (every payment dated `now`, making them all stack
 * on today in the monthly earnings view) stays fixed in one place.
 *
 * v1 contract: N installments, monthly cadence, equal amount per
 * installment. The wider "trainer-configurable plans (5/10/N
 * installments, custom intervals, custom amounts)" design will replace
 * this with a richer builder — until then, the existing 2/3 payment
 * checkboxes call this with numberOfPayments = 2 | 3.
 *
 * The `date` field is the actual booking date for the payment — used
 * by the monthly earnings chart and any "when was this earned"
 * grouping. Was previously set to `now()` for every payment, hence the
 * Tannaz bug where all 3 payments collapsed onto the day of re-up.
 */

import { addMonths, addWeeks } from "date-fns";

/**
 * Cadence between successive installments. Drives how subsequent
 * payment dates are derived from the anchor:
 *   - "monthly"  → anchorDate + i months
 *   - "biweekly" → anchorDate + (i * 2) weeks
 *   - "weekly"   → anchorDate + i weeks
 *
 * Custom-date plans (e.g. "1st and 15th of each month") are
 * deliberately out of scope for v1.
 */
export type PaymentCadence = "monthly" | "biweekly" | "weekly";

export const PAYMENT_CADENCE_LABELS: Record<PaymentCadence, string> = {
  monthly: "Monthly",
  biweekly: "Every 2 weeks",
  weekly: "Weekly",
};

export interface SplitPaymentInput {
  /** Total package price. Will be divided equally across installments
      when `equalSplit=true`, OR used as the per-installment amount when
      false (for the manual AddEarningsDialog flow that pre-divides). */
  totalAmount: number;
  /** Whether totalAmount should be divided by numberOfPayments before
      being attached to each row. Re-up flows pass true (the trainer
      enters package total). AddEarningsDialog passes false (the trainer
      enters per-payment amount already). */
  equalSplit: boolean;
  numberOfPayments: number;
  /** The anchor date for payment 1. Subsequent payments derive from
      this per the cadence rule. Pass the trainer's intent — usually
      `new Date()` for "starting today." */
  anchorDate: Date;
  /** Cadence between installments. Defaults to "monthly" so existing
      callers that haven't been updated yet keep their semantics. */
  cadence?: PaymentCadence;
}

export interface SplitPaymentRowSpec {
  /** 1-indexed installment number for labeling ("Payment 2 of 3"). */
  paymentNumber: number;
  /** Total installments in the plan, for labeling. */
  totalPayments: number;
  /** Dollar amount for this installment. */
  amount: number;
  /** ISO-formatted booking date — anchorDate + (paymentNumber - 1) months. */
  dateIso: string;
  /** Booking date as a Date instance, for pay-period derivation. */
  date: Date;
}

/**
 * Build the row specs for an N-installment plan. Doesn't insert
 * anything — callers attach their own metadata (client_id, user_id,
 * notes, category, pay_period_*) before sending to supabase.
 *
 * @example
 *   const rows = buildSplitPaymentRows({
 *     totalAmount: 2025,
 *     equalSplit: true,
 *     numberOfPayments: 3,
 *     anchorDate: new Date(),
 *   });
 *   // rows[0].date = today, rows[1].date = +1mo, rows[2].date = +2mo
 *   // rows[i].amount = 675 each
 */
export function buildSplitPaymentRows({
  totalAmount,
  equalSplit,
  numberOfPayments,
  anchorDate,
  cadence = "monthly",
}: SplitPaymentInput): SplitPaymentRowSpec[] {
  if (numberOfPayments < 1) {
    throw new Error("numberOfPayments must be >= 1");
  }
  const amountPerPayment = equalSplit ? totalAmount / numberOfPayments : totalAmount;

  const rows: SplitPaymentRowSpec[] = [];
  for (let i = 0; i < numberOfPayments; i++) {
    const date = installmentDate(anchorDate, i, cadence);
    rows.push({
      paymentNumber: i + 1,
      totalPayments: numberOfPayments,
      amount: amountPerPayment,
      dateIso: date.toISOString(),
      date,
    });
  }
  return rows;
}

/**
 * Date for the i-th installment (0-indexed) given an anchor and
 * cadence. i=0 always returns the anchor unchanged.
 */
function installmentDate(anchor: Date, i: number, cadence: PaymentCadence): Date {
  if (i === 0) return anchor;
  switch (cadence) {
    case "monthly":
      return addMonths(anchor, i);
    case "biweekly":
      return addWeeks(anchor, i * 2);
    case "weekly":
      return addWeeks(anchor, i);
  }
}

/**
 * Convenience: render the "Payment X of Y" suffix consistently.
 * Returns empty string for single-payment plans so callers can
 * unconditionally append it.
 */
export function paymentSuffix(paymentNumber: number, totalPayments: number): string {
  if (totalPayments <= 1) return "";
  return `Payment ${paymentNumber} of ${totalPayments}`;
}
